import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { UserService } from './user.service';
import { CompanyService } from './company.service';
import { environment } from '../../../environments/environment';
import { EmployeeData, UpdateEmployeeRequest, LocationsList, Response } from '../interfaces';
import { ACTION, EMPLOYEE_ROLES, SUBJECT } from '../enums';
import { PermissionService } from './permission.service';

@Injectable({
  providedIn: 'root'
})
export class EmployeeService {
  employeesList$: BehaviorSubject<EmployeeData[]> = new BehaviorSubject<EmployeeData[]>([]);
  sortedEmployeesList$: BehaviorSubject<EmployeeData[]> = new BehaviorSubject<EmployeeData[]>([]);
  managersList$: BehaviorSubject<EmployeeData[]> = new BehaviorSubject<EmployeeData[]>([]);
  locationsList$: BehaviorSubject<LocationsList[]> = new BehaviorSubject<LocationsList[]>([]);
  selectedId: number | null = null;
  searchColumns: string[] = ['firstName', 'status', 'birthDateFrom', 'birthDateTo', 'titles', 'managers', 'projects', 'availability'];
  searchQueries: string[] = ['', '', '', '', '', '', '', ''];
  searchQueriesInit: string[] = { ...this.searchQueries };

  constructor(
    private readonly http: HttpClient,
    private readonly companyService: CompanyService,
    private readonly userService: UserService,
    private readonly permissionService: PermissionService
  ) {}

  getEmployeeObjectById(id: number) {
    return this.employeesList$.getValue().find((employee) => employee.id === id);
  }

  getEmployeeObjectByIds(ids: number[]) {
    return ids.map((id) => this.employeesList$.getValue().find((employee) => employee.id === id));
  }

  setManagersList() {
    const managers = this.employeesList$.getValue().filter((employee) => employee.isManager);
    this.managersList$.next(managers);
  }

  updateEmployeesList(): Subscription {
    return this.getEmployeesListRequest().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.companyService.isEmployeesUpToDate$.next(true);
          this.employeesList$.next(response.data);
          this.sortedEmployeesList$.next([...response.data]);
          this.setManagersList();
        }
      }
    });
  }

  // updateManagersList(): Subscription {
  //   return this.getEmployeesListRequest(true).subscribe({
  //     next: (response) => {
  //       if (response.success && response.data) {
  //         this.managersList$.next(response.data);
  //       }
  //     }
  //   });
  // }

  updateLocationsList(): Subscription {
    return this.getLocationsListRequest().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.locationsList$.next(response.data);
        }
      }
    });
  }

  getLocationsListRequest(): Observable<Response<LocationsList[]>> {
    return this.http.get<Response<LocationsList[]>>(`${environment.baseUrl}/info/locations`);
  }

  createEmployeeRequest(data: EmployeeData, file: File | null): Observable<Response<EmployeeData>> {
    const formData = new FormData();
    const jsonBlob = new Blob([JSON.stringify({ ...data, lastName: data.lastName || null })], { type: 'application/json' });

    formData.append('request', jsonBlob);
    if (file) {
      formData.append('picture', file);
    }

    return this.http.post<Response<EmployeeData>>(`${environment.baseUrl}/employees/create`, formData);
  }

  private getEmployeesListRequest(isManagers?: boolean): Observable<Response<EmployeeData[]>> {
    let query = '';
    if (isManagers) {
      query = '?roles=' + EMPLOYEE_ROLES.MANAGER;
    }

    return this.http.get<Response<EmployeeData[]>>(`${environment.baseUrl}/employees/list${query}`);
  }

  getEmployeeByIdRequest(id: number): Observable<Response<any>> {
    return this.http.get<Response<any>>(`${environment.baseUrl}/employees/${id}`);
  }

  updateEmployeeRequest(
    id: number,
    data: UpdateEmployeeRequest | any,
    file: File | null,
    logoRemoved: boolean = false
  ): Observable<Response<UpdateEmployeeRequest>> {
    const formData = new FormData();
    const jsonBlob = new Blob([JSON.stringify(data)], { type: 'application/json' });

    formData.append('request', jsonBlob);
    if (file) {
      formData.append('picture', file);
    } else if (logoRemoved) {
      const emptyFile = new File([''], '');
      formData.append('picture', emptyFile);
    }

    if (id == this.userService.profileInfo$.getValue().id && !this.permissionService.roleAbility.can(ACTION.EDIT, SUBJECT.EMPLOYEE)) {
      return this.http.patch<Response<UpdateEmployeeRequest>>(`${environment.baseUrl}/employees/me`, formData);
    } else {
      return this.http.put<Response<UpdateEmployeeRequest>>(`${environment.baseUrl}/employees/${id}`, formData);
    }
  }

  removeEmployeeRequest(id: number): Observable<Response<any>> {
    return this.http.delete<Response<any>>(`${environment.baseUrl}/employees/${id}`);
  }

  updateProfilePhoto(file: File): Observable<Response<EmployeeData>> {
    const formData = new FormData();
    formData.append('picture', file);

    return this.http.put<Response<EmployeeData>>(`${environment.baseUrl}/employees/me/picture`, formData);
  }

  clearData() {
    this.employeesList$.next([]);
    this.sortedEmployeesList$.next([]);
    this.searchQueries = this.searchQueriesInit;
  }
}
